import PropTypes from "prop-types";
import React, { useRef, useEffect } from "react";

import { ProductImageSize } from "../../../helpers/product-image-helpers";
import ProductImage from "../shared/product-image";
import { productImagesPropType, productPropType } from "../../propTypes/product-prop-type";

import { FetchPriority } from "rtr-constants";

const PDPFeatureImage = ({
  imageGroup,
  isMobileViewport = null,
  index = 0,
  onClick,
  onVisible,
  lazy = false,
  altText = "",
}) => {
  const featuredImageRef = useRef(null);

  useEffect(() => {
    if (onVisible) {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              onVisible(index);
            }
          });
        },
        { threshold: 0.1 }
      );

      const currentImg = featuredImageRef.current;

      const observeWithDelay = () => {
        if (currentImg) {
          observer.observe(currentImg);
        }
      };

      // Short delay to allow for the image to be rendered before observing
      const delay = setTimeout(observeWithDelay, 200);

      return () => {
        clearTimeout(delay);
        if (currentImg) {
          observer.unobserve(currentImg);
        }
      };
    }
  }, [index, onVisible]);

  const isImageOutsideViewport = lazy || (isMobileViewport && index > 1);

  const renderProductImage = () => (
    <ProductImage
      altText={altText}
      className="featured-image"
      draggable={false}
      imageGroup={imageGroup}
      imageSize={ProductImageSize.x1080}
      srcSetSizes={[ProductImageSize.x480, ProductImageSize.x800, ProductImageSize.x1080]}
      sizes={"(max-width: 480px) 80vw, (max-width: 1024px) 50vw, 1080px"} // ideally the browser would use src as a default if the srcset media width doesn't match, but it's not always smart enough so specificying a size for 1080 here
      item-prop="image"
      ref={featuredImageRef}
      loading={isImageOutsideViewport ? "lazy" : "eager"}
      fetchPriority={isImageOutsideViewport ? FetchPriority.LOW : FetchPriority.HIGH}
    />
  );

  const renderProductImageAsButton = () => (
    <button className="button-img-wrapper" onClick={() => onClick(index, featuredImageRef)}>
      {renderProductImage()}
    </button>
  );

  return onClick ? renderProductImageAsButton() : renderProductImage();
};

PDPFeatureImage.displayName = "PDPFeatureImage";
PDPFeatureImage.propTypes = {
  imageGroup: productImagesPropType.isRequired,
  product: productPropType,
  isMobileViewport: PropTypes.bool,
  index: PropTypes.number,
  onClick: PropTypes.func,
  onVisible: PropTypes.func,
};

export default PDPFeatureImage;
